import { useMemo } from 'react';

import PropTypes from 'prop-types';

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import useToggledState from '@zenbusiness/application-commons-hooks/useToggledState';
import { FormattedMessage, useIntl } from '@zenbusiness/application-commons-intl';
import Banner from '@zenbusiness/zui/Banner';
import Text from '@zenbusiness/zui/Text';

import { VeloAIAssistant } from '#client/components/VeloAIAssistant';
import { RELEASE_BUSINESS_PROFILE_V2 } from '#client/constants/experimentsFlags';

import * as Styled from './BusinessProfile.style';
import { Breadcrumbs } from './components/Breadcrumbs';
import { InfoSection } from './components/InfoSection';
import { getInfos } from './helpers';

const LOCALIZED_TEXT = {
  heading: 'businessProfile.heading',
  bannerTitle: 'businessProfile.banner.title',
  bannerActionLabel: 'businessProfile.banner.actionLabel',
  aiAssistant: {
    title: 'businessProfile.aiAssistant.title',
    subtitle: 'businessProfile.aiAssistant.greeting',
  },
};

function BusinessProfile({ registeredAgents, businessEntity }) {
  const { formatMessage: fm } = useIntl();
  const showAIAssistant = useExperimentFlag(RELEASE_BUSINESS_PROFILE_V2, false);

  const { state: showAssistant, on: handleShowAssistant, off: handleCloseAssistant } = useToggledState(false);

  const {
    name,
    addresses: { mailing, principal },
    formationDate,
    domesticJurisdiction,
    ein,
  } = businessEntity;

  const infos = useMemo(
    () =>
      getInfos({
        principal,
        mailing,
        formationDate,
        domesticJurisdiction,
        registeredAgents,
        ein,
        fm,
      }),
    [principal, mailing, formationDate, domesticJurisdiction, registeredAgents, ein, fm],
  );

  return (
    <>
      <Styled.Root>
        <Breadcrumbs />
        <Styled.Header title={<Text size="3xl">{name}</Text>} />
        {showAIAssistant && (
          <Banner
            title={fm({ id: LOCALIZED_TEXT.bannerTitle })}
            clickstreamProps={{ appSection: 'business_profile' }}
            slotProps={{
              primaryAction: {
                label: fm({ id: LOCALIZED_TEXT.bannerActionLabel }),
                startIcon: <AutoAwesomeIcon />,
                size: 'sm',
                color: 'tertiary',
                onClick: handleShowAssistant,
              },
            }}
          />
        )}
        <Styled.Content size="xl">
          <Text size="xl" weight="medium">
            <FormattedMessage id={LOCALIZED_TEXT.heading} />
          </Text>
          <InfoSection infos={infos} />
        </Styled.Content>
      </Styled.Root>
      {showAIAssistant && (
        <VeloAIAssistant
          eventingProperties={{
            aiProjectName: 'Update My Info - Velo AI Assistant',
            elementName: 'update_my_info_velo_ai_assistant',
            elementText: 'Update My Information via Velo AI Assistant',
            elementValue: 'update_my_info_velo_ai_assistant',
          }}
          onClose={handleCloseAssistant}
          open={showAssistant}
          promptSlug="tool-input-mapping-business-profile-chang"
          queryType="businessProfile"
          subtitle={LOCALIZED_TEXT.aiAssistant.subtitle}
          title={LOCALIZED_TEXT.aiAssistant.title}
        />
      )}
    </>
  );
}

export default BusinessProfile;

BusinessProfile.propTypes = {
  registeredAgents: PropTypes.arrayOf(PropTypes.shape({})),
  businessEntity: PropTypes.shape({
    name: PropTypes.string.isRequired,
    addresses: PropTypes.shape({
      mailing: PropTypes.shape({}),
      principal: PropTypes.shape({}),
    }),
    formationDate: PropTypes.string,
    domesticJurisdiction: PropTypes.string,
    ein: PropTypes.string,
  }),
};
